import { api } from "./api";

const WEB_URL = import.meta.env.VITE_WEB_URL || "http://localhost:3002";

export interface Plan {
  id: string;

  key: string;

  name: string;

  type: "dedicated" | "lite";

  cores: number;

  ram: number;

  bandwidth: number;

  maxPlatforms: number;

  videoQuality: string;

  videoBitrate: number;

  monthlyPrice: number;

  weeklyStreamingHours: number | null;

  tier: number | null;

  isActive: boolean;

  /** Renditions simultáneas (calidades de salida distintas) que permite el plan
   * (1/3/5 según plan). null en respuestas viejas → tratar como 1. */
  maxRenditions: number | null;
}

export interface UserSubscription {
  id: string;

  userId: number;

  planId: string;

  status: "pending" | "active" | "expired" | "cancelled";

  creemSubscriptionId: string | null;

  creemCustomerId: string | null;

  creemCheckoutId: string | null;

  startsAt: string;

  expiresAt: string;

  // ISO date of the last successful payment, or null if never paid (e.g. a
  // still-`pending` checkout). Backend serialises `paidAt: Date | null`.
  paidAt: string | null;

  autoRenew: boolean;

  plan: Plan;
}

interface PlanResponseItem {
  id: string;
  localPlanId: string | null;
  key: string | null;
  name: string;
  type: "dedicated" | "lite" | null;
  monthlyPrice: number;
  currency: string;
  cores: number | null;
  ram: number | null;
  bandwidth: number | null;
  maxPlatforms: number | null;
  videoQuality: string | null;
  videoBitrate: number | null;
  weeklyStreamingHours: number | null;
  tier: number | null;
  isActive: boolean;
  creemProductId: string | null;
  metadata: Record<string, string>;
  maxRenditions?: number | null;
}

class SubscriptionService {
  async getPlans(): Promise<Plan[]> {
    const result = await api.authGet<{ data: PlanResponseItem[] }>("/plans");

    return result.data
      .filter((p) => p.localPlanId && p.isActive)
      .map((p) => ({
        id: p.localPlanId!,
        key: p.key ?? "",
        name: p.name,
        type: (p.type ?? "dedicated") as "dedicated" | "lite",
        cores: p.cores ?? 0,
        ram: p.ram ?? 0,
        bandwidth: p.bandwidth ?? 0,
        maxPlatforms: p.maxPlatforms ?? 0,
        videoQuality: p.videoQuality ?? "",
        videoBitrate: p.videoBitrate ?? 0,
        monthlyPrice:
          typeof p.monthlyPrice === "string"
            ? Number(p.monthlyPrice)
            : p.monthlyPrice,
        weeklyStreamingHours: p.weeklyStreamingHours,
        tier: p.tier,
        isActive: p.isActive,
        maxRenditions: p.maxRenditions ?? null,
      }));
  }

  async getMySubscriptions(): Promise<UserSubscription[]> {
    const result = await api.authGet<{ data: UserSubscription[] }>(
      "/subscriptions",
    );

    return result.data;
  }

  async createCheckoutSession(
    planId: string,
  ): Promise<{ checkoutId: string; url: string }> {
    const successUrl = `${WEB_URL}/checkout/callback?checkout_id={CHECKOUT_ID}&source=desktop`;

    const cancelUrl = `${WEB_URL}/checkout/callback?cancelled=true&source=desktop`;

    const result = await api.authPost<{
      data: { checkoutId: string; url: string };
    }>("/subscriptions/checkout", { planId, successUrl, cancelUrl });

    return result.data;
  }

  async createWorkspaceAddonCheckout(): Promise<{
    checkoutId: string;
    url: string;
  }> {
    const successUrl = `${WEB_URL}/checkout/callback?checkout_id={CHECKOUT_ID}&source=desktop`;

    const cancelUrl = `${WEB_URL}/checkout/callback?cancelled=true&source=desktop`;

    const result = await api.authPost<{
      data: { checkoutId: string; url: string };
    }>("/subscriptions/addons/workspace/checkout", { successUrl, cancelUrl });

    return result.data;
  }

  async getMyAddons(): Promise<{
    addons: {
      id: string;
      addonKey: string;
      quantity: number;
      status: string;
      expiresAt: string | null;
    }[];
    extraConcurrentWorkspaces: number;
  }> {
    const result = await api.authGet<{
      data: {
        addons: {
          id: string;
          addonKey: string;
          quantity: number;
          status: string;
          expiresAt: string | null;
        }[];
        extraConcurrentWorkspaces: number;
      };
    }>("/subscriptions/addons");

    return result.data;
  }

  async confirmCheckout(checkoutId: string): Promise<void> {
    await api.authPost<{ data: { message: string } }>(
      "/subscriptions/confirm",
      { checkoutId },
    );
  }

  async cancelSubscription(subscriptionId: string): Promise<void> {
    await api.authPost<unknown>(`/subscriptions/${subscriptionId}/cancel`, {});
  }
}

export const subscriptionService = new SubscriptionService();

export default subscriptionService;
